// © Microsoft Corporation. All rights reserved.

import { ApiErrors, appErrorLogger, fetchBlobClient, generateQueryStringByKeyVals, getParameter } from "../utils";
import { AppError, logger } from "@msnews/diagnostics";
import { Environment, appEnvironment, getLocalStorage, getOcid } from "@msnews/core";
import { MY_REACTIONIDS, MY_REACTION_PREFIX } from "../Constants";

import { SocialReactions } from "../schema/SocialBarData";
import { SocialServiceMapper } from "../mapper/SocialServiceMapper";
import { User } from "../schema/User";
import { enums } from "../schema/enums";
import { getLocationSearch } from "@msnews/isomorphic-util";

export enum ItemTypes {
    contents = "contents",
    comments = "comments",
    users = "users"
}

export enum ContentType {
    article = "article",
    slideshow = "slideshow",
    video = "video"
}

export const saveLocalContentReaction = (id: string, reactionType: SocialReactions, reset: boolean, localStorageSave?: (id: string, reaction?: string) => void): void => {
    if (localStorageSave) {
        localStorageSave(id, reset ? null : reactionType);
    } else if (getLocalStorage().supported) {
        if (reset) {
            getLocalStorage().setItem(`${MY_REACTION_PREFIX}${id}`, ""); // Compatibility
            getLocalStorage().removeItem(`${MY_REACTION_PREFIX}${id}`);
            const myReactionIds = getLocalStorage().getItem(MY_REACTIONIDS);
            let ids = myReactionIds ? myReactionIds.split(",") : [];
            ids = ids.filter(x => x != id);
            getLocalStorage().setItem(MY_REACTIONIDS, ids.join(","));
        } else if (reactionType) {
            const myReactionIds = getLocalStorage().getItem(MY_REACTIONIDS);
            let ids = myReactionIds ? myReactionIds.split(",") : [];
            // Cleanup local storage
            // If local storage exceed more than 200 ids remove the first half 100
            if (ids.length > 200) {
                ids = ids.filter((x, y) => {
                    if (y < 100) {
                        getLocalStorage().removeItem(`${MY_REACTION_PREFIX}${x}`);
                        return false;
                    }
                    return true;
                });
            }
            ids.push(id);
            getLocalStorage().setItem(`${MY_REACTION_PREFIX}${id}`, reactionType);
            getLocalStorage().setItem(MY_REACTIONIDS, ids.join(","));
        }
    }
};

export const getXPayToken = async (meId: string): Promise<string> => {
    const isPpeEnabled = getLocationSearch().toLowerCase().indexOf(enums.selectPpeParameter) > -1;
    if (appEnvironment.Environment === Environment.int && !isPpeEnabled) {
        console.warn("PPE environment must be enabled in INT environment for Subscription SDK to work");
    } else if (appEnvironment.Environment === Environment.prod && isPpeEnabled) {
        console.warn("PPE environment must not be enabled in PROD environment for Subscription SDK to work");
    }

    if (getLocalStorage().supported) {
        const env = isPpeEnabled ? "int" : "prod";
        const tokenStorageKey = `community_xpay_token_${meId}_${env}`;
        const tokenExpirationStorageKey = `community_xpay_token_expires_${meId}_${env}`;

        const storedToken = getLocalStorage().getItem(tokenStorageKey);
        if (storedToken) {
            const tokenExpires = getLocalStorage().getItem(tokenExpirationStorageKey);
            if (tokenExpires && ((new Date(parseInt(tokenExpires))).valueOf() - Date.now()) > 10000) {  // more than ten seconds to expiration
                return storedToken;
            }
        }

        const response = await SocialService.getXPayTokenInfo();
        getLocalStorage().setItem(tokenStorageKey, response.accessToken);
        getLocalStorage().setItem(tokenExpirationStorageKey, (Date.now() + response.expiresIn*1000).toString());
        return response.accessToken;
    } else {
        const response = await SocialService.getXPayTokenInfo();
        return response.accessToken;
    }
};

export class SocialService {
    private static domain = enums.externalUrls.api;
    private static sendAuthHeader: string;
    private static get msaAccessToken(): string {
        return getParameter("msaAccessToken");
    }
    private static get muid(): string {
        return getParameter("muid");
    }
    private static get fdhead(): string {
        return getParameter("fdhead") || (appEnvironment.CurrentRequestTargetScope &&
            appEnvironment.CurrentRequestTargetScope.pageExperiments &&
            appEnvironment.CurrentRequestTargetScope.pageExperiments.join());
    }
    private static get ocid(): string {
        return getParameter("ocid") || getOcid();
    }
    private static endpoints = {
        contents: SocialService.domain.concat("urls/"),
        providerContents: SocialService.domain.concat("contents"),
        comments: SocialService.domain.concat("comments/"),
        users: SocialService.domain.concat("users/"),
        systags: "https://api.msn.com/graph/systags",
        typingUpdates: "https://assets.msn.com/labs/api/"
    };

    constructor(sendAuthHeader) {
        SocialService.sendAuthHeader = sendAuthHeader;
    }

    // SocialService Config
    public static setDomain(newDomain) {
        this.domain = newDomain;
        this.endpoints = {
            ...this.endpoints,
            contents: SocialService.domain.concat("urls/"),
            providerContents: SocialService.domain.concat("contents"),
            comments: SocialService.domain.concat("comments/"),
            users: SocialService.domain.concat("users/")
        };
    }

    // USER
    public static async fetchMe(verify): Promise<User> {
        return SocialService.getUserById("me", verify);
    }

    public static async deleteMyData(deletingData): Promise<User> {
        const queryParams = {
            ...deletingData,
            profile: "social"
        };
        return SocialService.DELETE(SocialService.endpoints.users + "me/", queryParams);
    }

    public static async getUserById(userId: string, verify = false) {
        return SocialService.GET(SocialService.endpoints.users + userId, { version: "1.1", profile: "social", verify: verify })
            .then((data) => SocialServiceMapper.convertUser(data || null));
    }

    public static async getXPayTokenInfo() {
        return SocialService.GET(SocialService.domain + "xpaytoken");
    }

    public static async getUserProfile(userId: string) {
        let user = {
            followers: undefined,
            following: undefined,
            comments: undefined
        };
        return Promise.all([
            SocialService.GET(SocialService.endpoints.users + userId, { version: "1.1", profile: "social" }) // get user
                .then((data) => {
                    user = {
                        ...user,
                        ...SocialServiceMapper.convertUser(data || null)
                    };
                }),
            SocialService.getFollowersByUserId(userId).then((data) => {
                user.followers = data;
            }),
            SocialService.getFollowingsByUserId(userId).then((data) => {
                user.following = data;
            }),
            SocialService.getCommentsByUserId(userId).then((data) => {
                user.comments = data;
            })
        ]).then((results) => {
            return user;
        }).catch((data) => {
            logger.logError("Error getting user profile: " + data.statusText);
        });
    }

    public static async getUserContents(userId: string, market: string, premium?: boolean, args: any = {}) {
        const params: any = {
            market,
            $top: args.top || 20,
            $skip: args.skip || 0,
            $orderby: args.orderBy || "Time"
        };
        if (premium) {
            params.productType = "premium";
        }
        return SocialService.GET(SocialService.endpoints.users + userId + "/contents", params);
    }

    public static async appealMe() {
        return SocialService.POST(SocialService.endpoints.users + "me/appeals", {});
    }

    public static async appealMyPicture() {
        return SocialService.POST(SocialService.endpoints.users + "me/pictureAppeals", {});
    }

    public static async getFollowersByUserId(userId, top = 10, skip = 0): Promise<any> {
        const queryParams = {
            $top: top,
            $skip: skip
        };
        return SocialService.GET(SocialService.endpoints.users + userId + "/followers", queryParams).then(data => SocialServiceMapper.convertUserList(data));
    }

    public static async getFollowingsByUserId(userId, top = 10, skip = 0): Promise<any> {
        const queryParams = {
            $top: top,
            $skip: skip
        };
        return SocialService.GET(SocialService.endpoints.users + userId + "/followings", queryParams).then(data => SocialServiceMapper.convertUserList(data));
    }

    /** @param userInfo Used to create a matching user */
    public static async createUser(notUserAvatar?: boolean) {
        const postBody = { notUseAvatar: notUserAvatar || false };
        return SocialService.POST(SocialService.endpoints.users + "me", postBody, { profile: "social" }, undefined)
            .then((data) => SocialServiceMapper.convertUser(data || null))
            .catch(e => { return e.json().then(error => { throw error; }); });
    }

    /** @param isPrivateProfile Can be one of: true, false */
    public static async updateUserSettings(userId: string, isPrivateProfile: boolean) {
        const queryParams = {};
        const postBody = {};
        postBody[userId] = {
            isPrivateProfile: isPrivateProfile
        };
        return SocialService.PUT(SocialService.endpoints.users + userId + "/settings", postBody, queryParams, userId);
    }
    public static async followUser(userId: string, targetId: string) {
        return SocialService.POST(SocialService.endpoints.users + targetId + "/followers/" + userId, {}, {}, userId);
    }
    public static async unfollowUser(userId: string, targetId: string) {
        return SocialService.DELETE(SocialService.endpoints.users + targetId + "/followers/" + userId, {}, userId);
    }
    public static async getUserIsFollowed(userId: string, targetId: string) {
        return new Promise((resolve, reject) => {
            if (!userId) {
                resolve(null);
                return;
            }
            SocialService.GET(SocialService.endpoints.users + targetId + "/followers/" + userId, {})
                .then((data) => resolve(true))
                .catch((data) => resolve(false));
        });
    }
    public static async reportUser(userId: string, reportItemType: string, reportType: string, intellectualPropertyReportParams?: any) {
        const urlPart = reportItemType == "ProfilePicture" ? "PictureAbusereports" : "NameAbusereports";

        const { reportMessage, intellectualPropertyDetails } = intellectualPropertyReportParams || {};
        const postBody = {
            ReportType: reportType,
            ReportMessage: reportMessage || "",
            IntellectualPropertyDetails: intellectualPropertyDetails
        };
        try {
            return SocialService.POST(SocialService.endpoints.users + userId + "/" + urlPart, postBody, {});
        } catch (e) {
            logger.logError(e);
        }
    }
    public static async getUserNotifications() {
        const queryParams = {
            onlynotification: true,
            read: false,
            $top: 5,
            $skip: 0
        };
        return SocialService.GET(SocialService.endpoints.users + "me/activities", queryParams)
            .then((data) => SocialServiceMapper.convertUserActivities(data || null));
    }
    public static async getUserActivities(top, skip) {
        const queryParams = {
            $top: top || 50,
            $skip: skip || 0
        };
        return SocialService.GET(SocialService.endpoints.users + "me/activities", queryParams)
            .then((data) => SocialServiceMapper.convertUserActivities(data || null));
    }
    public static async updateUserActivityStatus(id: string, read = true) {
        const postBody = {
            id: id,
            read: read
        };
        return SocialService.POST(SocialService.endpoints.users + "me/activities", postBody);
    }

    // CONTENT
    public static async getContentById(cmsid: string, market?: string) {
        const params = this.isURL(cmsid) ? {
            url: cmsid,
            market,
            version: "1.1"
        } : {
            cmsid,
            market,
            version: "1.1"
        };
        if (!market) {
            delete params.market;
        }
        return SocialService.GET(SocialService.endpoints.contents, params)
            .then((data) => SocialServiceMapper.convertContent(data || null));
    }

    public static getProviderContents(providerId: string, market: string, contentType?: ContentType, partnerTag?: string, args: any = {}) {
        const params: any = {
            providerId,
            market,
            apiKey: appEnvironment.OneServiceApiKey,
            $top: args.top || 20,
            $skip: args.skip || 0,
            $orderby: args.orderBy || "Time desc"
        };
        if (contentType) {
            params.contentType = contentType;
        }
        if (partnerTag) {
            params.partnerTag = partnerTag;
        }
        return SocialService.GET(SocialService.endpoints.providerContents, params);
    }

    // https://stackoverflow.com/questions/8667070/javascript-regular-expression-to-validate-url
    private static isURL(value: string) {
        return /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i.test(value);
    }

    // COMMENT
    public static async getCommentById(commentId) {
        return SocialService.GET(SocialService.endpoints.comments + commentId)
            .then((data) => SocialServiceMapper.convertComment(data || null));
    }
    public static async getCommentsByContentId(contentId, args) {
        const queryParams = {
            contentId: contentId,
            $top: args.top || 50,
            $skip: args.skip || 0,
            $orderby: args.orderBy || args.sortType || "Rating"
        };
        return SocialService.GET(SocialService.endpoints.comments, queryParams)
            .then((data) => {
                // Calculate comment status
                let commentStatus = "On";
                try {
                    const contentCommentStatus = data.links.find((link) => link.type == "Content").item.commentStatus;
                    commentStatus = contentCommentStatus || commentStatus;
                } catch (e) {
                    logger.logError("Invalid content data in comments response.");
                }

                // Crate commentsList with pagination info and commentStatus
                try {
                    const commentsObj = SocialServiceMapper.convertCommentList(data || null);
                    commentsObj.commentStatus = commentStatus;

                    return commentsObj;
                } catch (e) {
                    logger.logError("Invalid comment list data in comments response.");

                    return {
                        commentStatus: "Off",
                        list: []
                    };
                }
            });
    }
    public static async getCommentsByParentId(parentCommentId, args) {
        const queryParams = {
            parentId: parentCommentId,
            $top: args.top || 10,
            $skip: args.skip || 0,
            $orderby: args.orderBy || "Time"
        };
        return SocialService.GET(SocialService.endpoints.comments, queryParams)
            .then((data) => SocialServiceMapper.convertCommentList(data || null));
    }
    public static async getCommentsByAnchorCommentId(anchorCommentId, args) {
        const queryParams = {
            anchorId: anchorCommentId,
            $top: args.top || 10,
            $skip: args.skip || 0,
            $orderby: args.orderBy || "Time"
        };
        return SocialService.GET(SocialService.endpoints.comments, queryParams)
            .then((data) => SocialServiceMapper.convertCommentList(data || null));
    }
    public static async getCommentsByUserId(userId, top = 10, skip = 0, orderBy = "Time"): Promise<any> {
        const queryParams = {
            userId: userId,
            $top: top,
            $skip: skip,
            $orderby: orderBy
        };
        return SocialService.GET(SocialService.endpoints.comments, queryParams)
            .then((data) => SocialServiceMapper.convertCommentList(data || null));
    }
    public static async postComment(contentId, userId, commentBody, commentParentId = undefined, commentMentionReplyId = undefined) {
        const postBody = {
            ContentId: contentId,
            Body: commentBody,
            parentId: commentParentId && commentParentId != contentId ? commentParentId : undefined,
            mentionReply: commentMentionReplyId
        };
        return new Promise((resolve, reject) => {
            SocialService.POST(SocialService.endpoints.comments, postBody, {}, userId)
                .then((data) => {
                    resolve(SocialServiceMapper.convertComment(data || null));
                }, (response) => {
                    response.json().then(data => {
                        reject(data);
                    }).catch(e => {
                        logger.logError("Error getting comment info: " + response.statusText);
                        reject();
                    });
                });
        });
    }
    public static async deleteComment(commentId) {
        return SocialService.DELETE(SocialService.endpoints.comments + commentId, {});
    }
    public static async appealComment(commentId) {
        return SocialService.POST(SocialService.endpoints.comments + commentId + "/appeals", {});
    }
    public static async reportComment(commentId, reportType, intellectualPropertyReportParams?) {
        const { reportMessage, intellectualPropertyDetails } = intellectualPropertyReportParams || {};
        const postBody = {
            ReportType: reportType,
            ReportMessage: reportMessage || "",
            IntellectualPropertyDetails: intellectualPropertyDetails
        };
        try {
            return SocialService.POST(SocialService.endpoints.comments + commentId + "/abusereports", postBody, {});
        } catch (e) {
            logger.logError(e);
        }
    }
    public static async uploadCopyrightFile(file): Promise<string> {
        const sasTokenInfo = await SocialService.GET(SocialService.endpoints.users + "me/UploadSasToken", { type: "ContentReport" });
        const AzureStorage = await fetchBlobClient();
        const blobUri = `https://${sasTokenInfo.accountUrl.split("?")[0]}`;
        const blobService = AzureStorage.Blob.createBlobServiceWithSas(blobUri, sasTokenInfo.sasString);
        return new Promise((res, rej) => {
            blobService.createBlockBlobFromBrowserFile(sasTokenInfo.container, sasTokenInfo.blobName, file, async (error, result, response) => {
                if (error) {
                    return rej(error);
                }
                return res(sasTokenInfo.id);
            });
        });
    }

    // ITEMS
    public static async rate(item, id, rateValue, forceRate = false, localStorageSave?: (id: string, reaction?: string) => void) {
        const queryParams = {};
        const postBody = {
            subType: rateValue
        };
        try {
            const data = await SocialService.POST(SocialService.endpoints[item] + id + "/reactions", postBody, queryParams);
            saveLocalContentReaction(id, rateValue, false, localStorageSave);
            return SocialServiceMapper.convertRatingToRateValue(data || null);
        } catch (e) {
            logger.logError(e);
            return rateValue;
        }
    }
    public static async resetRating(item, id, localStorageSave?: (id: string, reaction?: string) => void) {
        try {
            saveLocalContentReaction(id, null, true, localStorageSave);
            return SocialService.DELETE(SocialService.endpoints[item] + id + "/reactions", {});
        } catch (e) {
            logger.logError(e);
        }
    }
    public static async getFriendRatings(item, id) {
        return SocialService.GET(SocialService.endpoints[item] + id + "/reactions", { friends: true, $top: 2 });
    }
    public static async getRating(item, id) {
        return SocialService.GET(SocialService.endpoints[item] + id + "/reactions", {})
            .then((data) => SocialServiceMapper.convertRatingToRateValue(data || null));
    }
    public static async getRatingBreakdown(item, id) {
        return SocialService.GET(SocialService.endpoints[item] + id + "/reactions", { others: true })
            .then((data) => SocialServiceMapper.convertReactionsResponseToReactionBreakdownData(data || null));
    }

    private static prepareHubString(contentId: string) {
        return contentId.replace(/-/g, "_"); // signalR won't accept hubs with dashes
    }

    // Realtime Typing Updates
    public static async getSignalRConnectionInfo(contentId: string) {
        const hub = this.prepareHubString(contentId);
        return SocialService.POST(SocialService.endpoints.typingUpdates + "RealtimeTypingNegotiate", {}, { hub });
    }

    public static async broadcastCommentNotification(contentId: string, commentId: string, commentParentId: string) {
        const hub = this.prepareHubString(contentId);
        const data = {
            type: "comment-notification",
            data: {
                commentId,
                commentParentId
            }
        };
        return SocialService.POST(SocialService.endpoints.typingUpdates + "RealtimeTypingBroadcast", data, { hub }).then(res => { }, err => { });
    }

    public static async broadcastTypingNotification(contentId: string, id: string, name: string, profileImageUrl: string) {
        const hub = this.prepareHubString(contentId);
        const data = {
            type: "typing-notification",
            data: {
                id,
                name,
                profileImageUrl
            }
        };
        return SocialService.POST(SocialService.endpoints.typingUpdates + "RealtimeTypingBroadcast", data, { hub }, id).then(res => { }, err => { });
    }

    private static async GET(url, queryParams?, userId?) {
        queryParams = queryParams || {};
        const requestArgs = {
            queryParams: queryParams,
            authHeader: undefined,
            body: undefined,
            method: "GET"
        };
        if (userId && SocialService.sendAuthHeader) {
            requestArgs.authHeader = userId;
        }
        return this.xhr(url, requestArgs);
    }

    private static async POST(url, data, queryParams?, userId?, type = "POST") {
        queryParams = queryParams || {};
        const requestArgs = {
            method: type,
            queryParams: queryParams,
            body: data,
            authHeader: undefined
        };
        if (userId && SocialService.sendAuthHeader) {
            requestArgs.authHeader = userId;
        }
        return this.xhr(url, requestArgs);
    }

    private static async PUT(url, data, queryParams, userId?) {
        return SocialService.POST(url, data, queryParams, userId, "PUT");
    }

    private static async DELETE(url, queryParams, userId?) {
        queryParams = queryParams || {};
        const requestArgs = {
            method: "DELETE",
            queryParams: queryParams,
            authHeader: undefined,
            body: undefined
        };
        if (userId && SocialService.sendAuthHeader) {
            requestArgs.authHeader = userId;
        }
        return this.xhr(url, requestArgs);
    }

    private static cachedResponses: { [url: string]: Promise<Response> } = {};
    private static clearCache() {
        SocialService.cachedResponses = {};
    }

    private static async fetchRetry(url, options, retry = 2) {
        try {
            const res = await fetch(url, options);
            if (res.status >= 500) {
                throw new Error("Retry API error");
            }
            return res;
        } catch (err) {
            if (retry <= 1) {
                throw err;
            }
            return await SocialService.fetchRetry(url, options, retry - 1);
        }
    }

    private static fetchCachedResponse(url, options) {
        if (options && options.method == "GET" && url in SocialService.cachedResponses) {
            return SocialService.cachedResponses[url];
        }

        if (options && options.method != "GET") {
            SocialService.clearCache();
        }

        const result = SocialService.fetchRetry(url, options);
        if (options && options.method == "GET") {
            SocialService.cachedResponses[url] = result;
            setTimeout(() => {
                if (url in SocialService.cachedResponses) {
                    delete SocialService.cachedResponses[url];
                }
            }, 500);
        }

        return result;
    }

    private static async xhr(url, args = {
        queryParams: undefined,
        method: undefined,
        authHeader: undefined,
        body: undefined
    }): Promise<any> {
        // receives args: method, queryParams, headers, body
        args.queryParams.scn = appEnvironment.AppType === "windowsShell" ? "APP_ANON" : "MSNRPSAuth";
        args.queryParams.wrapodata = "false";

        const headers = args.method !== "GET" ? { "Content-Type": "application/json" } : {};
        if (args.authHeader) {
            headers["X-MSN-SCP-Auth"] = args.authHeader;
        }
        if (SocialService.msaAccessToken) {
            headers["UserTicketType"] = "2";
            headers["UserAuthToken"] = SocialService.msaAccessToken;
            headers["SiteName"] = "api.msn.com";
        }
        if (SocialService.muid) {
            headers["muid"] = "m-" + SocialService.muid;
        }
        if (SocialService.fdhead) {
            args.queryParams.fdhead = SocialService.fdhead;
        }
        if (SocialService.ocid) {
            args.queryParams.ocid = SocialService.ocid;
        }

        const queryString = generateQueryStringByKeyVals(args.queryParams);
        const fetchOptions: any = {
            method: args.method || "GET",
            headers,
            credentials: "include"
        };
        if (args.body) {
            fetchOptions.body = JSON.stringify(args.body);
        }

        return SocialService.fetchCachedResponse(url + queryString, fetchOptions).then(response => {
            if (response.status >= 200 && response.status < 300 || response.status == 401) {
                if (response.status == 204) {
                    return {};
                }
                try {
                    // Cloning for multiple consumers
                    return response.clone().json().then(result => {
                        if (result.value) {
                            result = result.value[0];
                        }
                        return result;
                    });
                } catch (e) {
                    this.onRequestError(url, args);
                    throw ({
                        status: response.status,
                        statusText: "INVALID_RESPONSE"
                    });
                }
            } else {
                if (response.status > 499 || response.status == 400) {
                    this.onRequestError(url, args);
                }
                throw response;
            }
        });
    }

    private static onRequestError(url, args): void {
        const resource = this.resourceForUrl(url);
        const appError: AppError = ApiErrors[resource] || ApiErrors.unknown;
        appErrorLogger.log({
            ...appError,
            message: "Request failed",
            pb: {
                ...appError.pb,
                customMessage: resource,
                url: url,
                method: args.method
            }
        } as AppError);
    }

    private static resourceForUrl(url): string {
        for (const [key, value] of Object.entries(ItemTypes)) {
            if (url.indexOf(`/${value}`) > 0) {
                return value;
            }
        }
        return "unknown";
    }
}